import React, { useMemo } from "react";
import { Box, Paper, Typography, Skeleton, Chip } from "@mui/material";
import { DataTrending24Regular, Timer24Regular, History24Regular } from "@fluentui/react-icons";
import { BarChart } from "@mui/x-charts/BarChart";
import { THEME, useOverviewData } from "./OverviewContext";
import { CTLExploreDataButton } from "../utils/CTLExploreDataButton";
import { generateMonthlyTrendQuery, generateMonthlyAvgDispositionQuery } from "../utils/ctlKustoQueries";
import { CTLInfoTooltip } from "../utils/CTLInfoTooltip";

const MonthlyInsights: React.FC = () => {
  const { data, isLoading } = useOverviewData();

  // Use API data directly - always shows last 12 months regardless of time range filter
  const monthlyRequestCounts = data?.monthlyRequestCounts ?? [];
  const monthlyAvgEndToEndDays = data?.monthlyAvgEndToEndDays ?? [];

  const monthLabels = useMemo(() => {
    return monthlyRequestCounts.map((m) => m.label);
  }, [monthlyRequestCounts]);

  const monthlyRequestValues = useMemo(() => {
    return monthlyRequestCounts.map((m) => m.count);
  }, [monthlyRequestCounts]);

  const e2eDaysValues = useMemo(() => {
    return monthlyAvgEndToEndDays.map((m) => m.avgDays);
  }, [monthlyAvgEndToEndDays]);

  const e2eLabels = useMemo(() => {
    return monthlyAvgEndToEndDays.map((m) => m.label);
  }, [monthlyAvgEndToEndDays]);

  // Use API-provided values for totals and changes
  const totalRequests = monthlyRequestCounts.reduce((sum, m) => sum + m.count, 0);
  const momChange = data?.monthOverMonthChange ?? 0;
  const latestAvgDays = data?.latestMonthAvgDays ?? 0;
  const avgDaysChange = data?.latestMonthAvgDaysChange ?? 0;

  if (isLoading) {
    return (
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "repeat(2, 1fr)" }, gap: 2 }}>
        <Skeleton variant="rectangular" height={360} sx={{ borderRadius: "12px" }} />
        <Skeleton variant="rectangular" height={360} sx={{ borderRadius: "12px" }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", xl: "repeat(2, minmax(0, 1fr))" },
        gap: 2
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: "12px",
          border: `1px solid ${THEME.border}`,
          backgroundColor: THEME.surface,
          minHeight: 360,
          display: "flex",
          flexDirection: "column"
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DataTrending24Regular style={{ color: THEME.primary }} />
            <Typography sx={{ fontSize: "16px", fontWeight: 700, color: THEME.textPrimary }}>
              Monthly Customer Requests Created
            </Typography>
            <CTLInfoTooltip
              title="Monthly Customer Requests Created"
              description="The demand trend — how many new customer requests arrived each month. Rising bars mean growing customer interest or a growing gap in the product; falling bars mean demand is easing."
              howItsCalculated="We group requests by the month they were raised and count them. TOTAL is the sum across the 12 months shown, and MoM is the change between the latest month and the month before it."
              note="This chart always shows the last 12 months, regardless of the date range selected above."
            />
            <Chip
              icon={<History24Regular style={{ fontSize: 14 }} />}
              label="Last 12 months"
              size="small"
              sx={{
                ml: 1,
                height: 22,
                fontSize: "11px",
                fontWeight: 600,
                backgroundColor: "#E7F1FF",
                color: THEME.primary,
                "& .MuiChip-icon": {
                  color: "inherit",
                  marginLeft: "6px"
                }
              }}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
              <Typography sx={{ fontSize: "14px", color: THEME.textSecondary }}>TOTAL</Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 700, color: THEME.textPrimary }}>
                {totalRequests.toLocaleString()}
              </Typography>
              <Typography sx={{ fontSize: "12px", color: momChange >= 0 ? THEME.success : THEME.error, fontWeight: 600 }}>
                MoM {momChange >= 0 ? "↗" : "↘"} {momChange >= 0 ? "+" : ""}{momChange}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ height: 260, width: "100%", flex: 1 }}>
          <BarChart
            xAxis={[{ scaleType: "band", data: monthLabels, tickLabelStyle: { fontSize: 11 } }]}
            yAxis={[{ tickNumber: 5 }]}
            series={[{ data: monthlyRequestValues, color: THEME.primary }]}
            height={260}
            margin={{ top: 20, right: 20, bottom: 30, left: 50 }}
            slotProps={{ legend: { hidden: true }, bar: { rx: 3, ry: 3 } }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            minHeight: 45,
            borderTop: `2px solid ${THEME.border}`,
            mt: 1,
            pt: 1
          }}
        >
          <CTLExploreDataButton
            query={generateMonthlyTrendQuery()}
            chartTitle="Monthly Customer Requests"
          />
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: "12px",
          border: `1px solid ${THEME.border}`,
          backgroundColor: THEME.surface,
          minHeight: 360,
          display: "flex",
          flexDirection: "column"
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Timer24Regular style={{ color: THEME.success }} />
            <Typography sx={{ fontSize: "16px", fontWeight: 700, color: THEME.textPrimary }}>
              Average Customer Request Time to Disposition by Month
            </Typography>
            <CTLInfoTooltip
              title="Average Time to Disposition by Month"
              description="Our responsiveness trend — the typical wait, in days, before a customer request reaches a decision. Shorter bars are better; a rising trend means customers are waiting longer than they used to."
              howItsCalculated="For each month we average the number of days between a request being raised and it reaching a decision. LATEST shows the most recent month and MoM shows how that compares with the previous month."
              note="Requests still awaiting a decision are not included, so the figure reflects completed journeys only. Always shows the last 12 months."
            />
            <Chip
              icon={<History24Regular style={{ fontSize: 14 }} />}
              label="Last 12 months"
              size="small"
              sx={{
                ml: 1,
                height: 22,
                fontSize: "11px",
                fontWeight: 600,
                backgroundColor: "#E7F1FF",
                color: THEME.primary,
                "& .MuiChip-icon": {
                  color: "inherit",
                  marginLeft: "6px"
                }
              }}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
              <Typography sx={{ fontSize: "14px", color: THEME.textSecondary }}>LATEST</Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 700, color: THEME.textPrimary }}>
                {latestAvgDays.toFixed(1)}d
              </Typography>
              <Typography sx={{ fontSize: "12px", color: avgDaysChange <= 0 ? THEME.success : THEME.error, fontWeight: 600 }}>
                MoM {avgDaysChange <= 0 ? "↘" : "↗"} {avgDaysChange > 0 ? "+" : ""}{avgDaysChange.toFixed(1)}d
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ height: 260, width: "100%", flex: 1 }}>
          <BarChart
            xAxis={[{ scaleType: "band", data: e2eLabels, tickLabelStyle: { fontSize: 11 } }]}
            yAxis={[{ tickNumber: 5, valueFormatter: (val: number) => `${val}d` }]}
            series={[{ data: e2eDaysValues, color: THEME.success }]}
            height={260}
            margin={{ top: 20, right: 20, bottom: 30, left: 50 }}
            slotProps={{ legend: { hidden: true }, bar: { rx: 3, ry: 3 } }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            minHeight: 45,
            borderTop: `2px solid ${THEME.border}`,
            mt: 1,
            pt: 1
          }}
        >
          <CTLExploreDataButton
            query={generateMonthlyAvgDispositionQuery()}
            chartTitle="Time to Disposition"
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default MonthlyInsights;
